import { MAX_WEB_SEARCH_DOMAINS, type WebSearchDomains, webDomainSchema } from "@esi/shared";
import { X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { t as translate } from "../../i18n/i18n.ts";

/**
 * Editors of the web search settings (openspec change `web-search`), shared by the project
 * settings and the backoffice: the allowed and blocked domain lists, and the oldest result.
 * Domains are checked as the server checks them (`webDomainSchema`): a host name, without
 * scheme, port, path or wildcard, normalised to lower case.
 */

const SHOWN = 3;

function listed(domains: readonly string[]): string {
  const head = domains.slice(0, SHOWN).join(", ");
  return domains.length > SHOWN
    ? translate("settings.behaviour.web.andMore", { list: head, count: domains.length - SHOWN })
    : head;
}

/** "Any domain", "Only a.com, b.com · never c.com"… */
export function describeDomains(value: unknown): string {
  const v = asDomains(value);
  if (v.include.length === 0 && v.exclude.length === 0) {
    return translate("settings.behaviour.web.anyDomain");
  }
  const parts: string[] = [];
  if (v.include.length > 0) {
    parts.push(translate("settings.behaviour.web.only", { domains: listed(v.include) }));
  }
  if (v.exclude.length > 0) {
    parts.push(
      translate(
        v.include.length > 0 ? "settings.behaviour.web.never" : "settings.behaviour.web.neverStart",
        { domains: listed(v.exclude) },
      ),
    );
  }
  return parts.join(" · ");
}

/** "30 days", or "No limit" when empty. */
export function describeMaxAge(value: unknown): string {
  return typeof value === "number"
    ? translate("settings.behaviour.web.days", { count: value })
    : translate("settings.behaviour.web.noLimit");
}

/** A stored value as both lists (an absent or malformed one is empty). */
export function asDomains(value: unknown): WebSearchDomains {
  const v = (typeof value === "object" && value !== null ? value : {}) as Partial<
    Record<"include" | "exclude", unknown>
  >;
  const list = (l: unknown) =>
    Array.isArray(l) ? l.filter((d): d is string => typeof d === "string") : [];
  return { include: list(v.include), exclude: list(v.exclude) };
}

export function DomainsEditor(props: {
  id: string;
  value: unknown;
  onChange: (value: WebSearchDomains) => void;
  max?: number;
}) {
  const { id, onChange } = props;
  const max = props.max ?? MAX_WEB_SEARCH_DOMAINS;
  const value = asDomains(props.value);
  return (
    <div className="flex flex-col gap-4">
      <DomainList
        id={`${id}-include`}
        label={translate("settings.behaviour.web.include")}
        hint={translate("settings.behaviour.web.includeHint")}
        domains={value.include}
        max={max}
        onChange={(include) => onChange({ ...value, include })}
      />
      <DomainList
        id={`${id}-exclude`}
        label={translate("settings.behaviour.web.exclude")}
        domains={value.exclude}
        max={max}
        onChange={(exclude) => onChange({ ...value, exclude })}
      />
    </div>
  );
}

/**
 * One list of domains as removable chips, with a field that adds one or several (separated by
 * spaces or commas) on Enter or "Add". An entry the server would refuse stays in the field,
 * with the reason.
 */
function DomainList(props: {
  id: string;
  label: string;
  hint?: string;
  domains: readonly string[];
  max: number;
  onChange: (domains: string[]) => void;
}) {
  const { id, label, hint, domains, max, onChange } = props;
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const add = () => {
    const entries = text.split(/[\s,;]+/).filter(Boolean);
    if (entries.length === 0) {
      return;
    }
    const next = [...domains];
    const kept: string[] = [];
    let problem: string | null = null;
    for (const entry of entries) {
      const parsed = webDomainSchema.safeParse(entry);
      if (!parsed.success) {
        kept.push(entry);
        problem ??= t("settings.behaviour.web.invalid", { domain: entry.slice(0, 80) });
      } else if (next.includes(parsed.data)) {
        problem ??= t("settings.behaviour.web.duplicate", { domain: parsed.data });
      } else if (next.length >= max) {
        kept.push(entry);
        problem ??= t("settings.behaviour.web.tooMany", { max });
      } else {
        next.push(parsed.data);
      }
    }
    setText(kept.join(" "));
    setError(problem);
    if (next.length !== domains.length) {
      onChange(next);
    }
  };
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ");
  return (
    <fieldset className="flex min-w-0 flex-col gap-2">
      <legend className="mb-1 text-sm font-medium text-fg">{label}</legend>
      {domains.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {domains.map((domain) => (
            <li
              key={domain}
              className="inline-flex h-7 max-w-full items-center gap-0.5 rounded-md border border-line bg-surface-sunken ps-2 text-[13px] text-fg"
            >
              <span className="truncate">{domain}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="text-fg-muted hover:text-fg"
                aria-label={t("settings.behaviour.web.remove", { domain })}
                onClick={() => onChange(domains.filter((d) => d !== domain))}
              >
                <X aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-fg-muted">{t("settings.behaviour.web.none")}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={`${id}-input`}
          aria-label={t("settings.behaviour.web.addTo", { list: label })}
          aria-invalid={error !== null}
          aria-describedby={describedBy || undefined}
          value={text}
          placeholder={t("settings.behaviour.web.placeholder")}
          autoComplete="off"
          spellCheck={false}
          className="min-w-0 flex-1 sm:max-w-72"
          onChange={(e) => {
            setText(e.currentTarget.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Adds the entry; never submits the setting's form.
              e.preventDefault();
              add();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={text.trim() === ""}
          aria-label={t("settings.behaviour.web.addTo", { list: label })}
          onClick={add}
        >
          {t("settings.behaviour.web.add")}
        </Button>
      </div>
      {hint ? (
        <p id={`${id}-hint`} className="text-xs text-fg-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="text-sm text-danger-fg">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
